// Work → Workload tab (Workload Intelligence + Work Health, Phase 1).
// Segmented control My · Team · Departments. "My" is for everyone with
// work:read once the tenant enables the feature; Team/Departments render only
// when the policy's `me.can_view_workload` says so (the server still enforces
// 403 + message on every call — hiding is UX only).
// Copy rules: describe work, never judge people; stale copy names calendar or
// business days per the policy's `stale_basis`; every band/health shows its
// reasons. The only percentage anywhere is
// utilization, and only for a person whose capacity is configured (Phase 2).
import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Loader2 } from 'lucide-react';
import { getApiErrorMessage } from '@/lib/apiError';
import { useWorkPolicy } from '@/features/work/api/hooks';
import { useMyWorkload } from '@/features/work/api/workloadHooks';
import type { StaleBasis } from '@/services/workloadService';
import { CapacityBlock } from './capacity';
import { BandChip, confidenceLabel } from './health';
import { NeedsAttention } from './NeedsAttention';
import { SummaryTiles, staleRule } from './tiles';
import { WorkloadDepartments, WorkloadTeam } from './WorkloadPeople';

export type WorkloadView = 'my' | 'team' | 'departments';
const VIEWS: WorkloadView[] = ['my', 'team', 'departments'];

export const WorkloadPage: React.FC<{ refreshKey?: number }> = ({ refreshKey = 0 }) => {
  const { t } = useTranslation('work');
  const tt = t as unknown as (key: string, opts?: Record<string, unknown>) => string;
  const [params, setParams] = useSearchParams();
  const { policy, me } = useWorkPolicy();
  const canViewTeam = !!me?.can_view_workload;

  const requested = params.get('view') as WorkloadView | null;
  const view: WorkloadView = requested && VIEWS.includes(requested) && (requested === 'my' || canViewTeam) ? requested : 'my';
  const departmentId = params.get('department') || '';
  const setView = (next: WorkloadView, dept?: string) => {
    const p = new URLSearchParams(params);
    if (next === 'my') p.delete('view'); else p.set('view', next);
    if (dept) p.set('department', dept); else p.delete('department');
    setParams(p, { replace: true });
  };
  const setDepartment = (id: string) => setView(id ? 'team' : (view === 'my' ? 'my' : view), id || undefined);

  const views: Array<{ key: WorkloadView; label: string }> = [
    { key: 'my', label: t('wl_view_my', { defaultValue: 'My' }) as string },
    ...(canViewTeam ? [
      { key: 'team' as WorkloadView, label: t('wl_view_team', { defaultValue: 'Team' }) as string },
      { key: 'departments' as WorkloadView, label: t('wl_view_departments', { defaultValue: 'Departments' }) as string },
    ] : []),
  ];

  const staleAfterDays = policy?.stale_after_days;
  const staleBasis = policy?.stale_basis || 'calendar_days';
  const dueSoonDays = policy?.due_soon_days;
  const showUtilization = policy?.workload_show_utilization !== false;

  return (
    <div className="space-y-4" data-testid="workload-page">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div role="group" aria-label={t('wl_view', { defaultValue: 'Workload view' }) as string}
          className="inline-flex rounded-md border border-border overflow-hidden text-sm" data-testid="workload-segments">
          {views.map((v) => (
            <button key={v.key} type="button" aria-pressed={view === v.key} onClick={() => setView(v.key)}
              className={`px-3 py-1.5 ${view === v.key ? 'bg-primary-500 text-white' : 'bg-background text-muted-foreground hover:bg-muted'}`}>
              {v.label}
            </button>
          ))}
        </div>
        {canViewTeam && me?.workload_scope_summary && (
          <span className="text-xs text-muted-foreground">
            {t('wl_scope', { defaultValue: 'Your workload scope: {{scope}}', scope: me.workload_scope_summary })}
          </span>
        )}
      </div>

      {view === 'my' && <MyWorkload refreshKey={refreshKey} staleAfterDays={staleAfterDays} staleBasis={staleBasis} dueSoonDays={dueSoonDays} tt={tt} showUtilization={showUtilization} />}
      {view === 'team' && (
        <WorkloadTeam departmentId={departmentId} onDepartment={setDepartment} refreshKey={refreshKey}
          staleAfterDays={staleAfterDays} staleBasis={staleBasis} dueSoonDays={dueSoonDays} />
      )}
      {view === 'departments' && <WorkloadDepartments onDepartment={(id) => setView('team', id)} refreshKey={refreshKey} />}
    </div>
  );
};

const MyWorkload: React.FC<{
  refreshKey: number;
  staleAfterDays?: number;
  staleBasis?: StaleBasis;
  dueSoonDays?: number;
  tt: (key: string, opts?: Record<string, unknown>) => string;
  showUtilization: boolean;
}> = ({ refreshKey, staleAfterDays, staleBasis, dueSoonDays, tt, showUtilization }) => {
  const { t } = useTranslation('work');
  const { data, loading, error } = useMyWorkload(true, refreshKey);
  useEffect(() => {
    if (error) toast.error(getApiErrorMessage(error, t('my_workload_load_failed', { defaultValue: 'Could not load your workload.' })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);
  const stale = data?.policy.stale_after_days ?? staleAfterDays;
  const basis = data?.policy.stale_basis ?? staleBasis;
  const due = data?.policy.due_soon_days ?? dueSoonDays;
  return (
    <div className="space-y-4" data-testid="my-workload">
      {loading && !data && (
        <p className="text-sm text-muted-foreground flex items-center gap-2" role="status">
          <Loader2 className="w-4 h-4 animate-spin" /> {t('common.loading', { defaultValue: 'Loading…' })}
        </p>
      )}
      <SummaryTiles totals={data?.summary} loading={loading && !data} staleAfterDays={stale} staleBasis={basis} dueSoonDays={due} />
      {data && (
        <div className="flex items-center gap-2 flex-wrap text-sm">
          <BandChip band={data.workload.band} reasons={data.workload.reasons} confidence={data.workload.confidence} />
          <span className="text-xs text-muted-foreground">{confidenceLabel(tt, data.workload.confidence)}</span>
          {data.summary.paused > 0 && (
            <span className="text-xs text-muted-foreground">· {t('paused_count', { defaultValue: '{{count}} on hold', count: data.summary.paused })}</span>
          )}
          {data.summary.upcoming > 0 && (
            <span className="text-xs text-muted-foreground">· {t('upcoming_count', { defaultValue: '{{count}} upcoming', count: data.summary.upcoming })}</span>
          )}
          {data.summary.collaborating > 0 && (
            <span className="text-xs text-muted-foreground">· {t('collaborating_count', { defaultValue: 'collaborating on {{count}}', count: data.summary.collaborating })}</span>
          )}
          {data.summary.review_queue > 0 && (
            <span className="text-xs text-muted-foreground">· {t('review_queue_count', { defaultValue: '{{count}} awaiting your review', count: data.summary.review_queue })}</span>
          )}
        </div>
      )}
      {data && showUtilization && <CapacityBlock capacity={data.capacity} />}
      <NeedsAttention items={data?.attention || []} loading={loading} filterable />
      {stale ? (
        <p className="text-xs text-muted-foreground" data-testid="stale-rule">{staleRule(tt, stale, basis)}</p>
      ) : null}
    </div>
  );
};

export default WorkloadPage;
